import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Textarea } from '../../ui/textarea';
import { Card, Pill, PrimaryButton } from '../ui';
import { adminApi } from '../../../pages/journey/journeyApi';

const inp = 'w-full border border-slate-200 rounded-xl px-3 py-2 text-sm';

const ReviewCard = ({ sub, catalog, onDone }) => {
  const mission = catalog.missions.find((m) => m.id === sub.mission_id) || { rubric: [] };
  const [scores, setScores] = useState({});
  const [comments, setComments] = useState({});
  const [f, setF] = useState({ strengths: '', improvements: '', comment: '', assign_extra: '' });
  const eligible = sub.requires_specialist ? catalog.staff.filter((x) => x.specialisms.length) : catalog.staff;
  const [reviewer, setReviewer] = useState(eligible[0]?.id);
  const allScored = mission.rubric.every((r) => scores[r.id]);
  const decide = async (decision) => {
    try {
      const criteria = mission.rubric.map((r) => ({ id: r.id, score: scores[r.id], comment: comments[r.id] || '' }));
      const r = await adminApi(`/submissions/${sub.id}/review`, { method: 'POST', body: { reviewer_id: reviewer, decision, criteria, ...f } });
      toast.success(`${decision === 'approve' ? 'Approved' : 'Revision requested'} · ${r.score}%`); onDone();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Card data-testid={`review-${sub.id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <p className="font-outfit font-semibold text-[#0b1f4d]">{sub.mission_title}</p>
        <Pill className="bg-slate-100 text-slate-600 capitalize">{sub.kind}</Pill>
        {sub.requires_specialist && <Pill className="bg-teal-50 text-teal-700">Specialist review</Pill>}
      </div>
      <p className="text-xs text-slate-500 mb-3">{sub.student_name} · attempt {sub.attempt} · {new Date(sub.created_at).toLocaleString('en-IN')}</p>
      <pre className="text-xs bg-slate-50 rounded-xl p-3 max-h-56 overflow-auto whitespace-pre-wrap" data-testid="review-content">{sub.content}</pre>
      <div className="grid md:grid-cols-2 gap-2 mt-3">
        {mission.rubric.map((r) => (
          <div key={r.id} className="text-xs border border-slate-100 rounded-lg p-2.5">
            <div className="flex justify-between items-center gap-2"><strong>{r.name}</strong>
              <div className="flex gap-1">{[1, 2, 3, 4].map((n) => (
                <button key={n} onClick={() => setScores({ ...scores, [r.id]: n })} data-testid={`review-score-${r.id}-${n}`}
                  className={`w-7 h-7 rounded-full border text-xs ${scores[r.id] === n ? 'bg-[#0b1f4d] text-white border-[#0b1f4d]' : 'border-slate-200'}`}>{n}</button>))}</div></div>
            <p className="text-slate-500 mt-1">{r.excellent}</p>
            <input value={comments[r.id] || ''} onChange={(e) => setComments({ ...comments, [r.id]: e.target.value })} placeholder="Comment (optional)" className="w-full mt-1 border-b border-slate-100 outline-none py-1" data-testid={`review-comment-${r.id}`} />
          </div>))}
      </div>
      <div className="grid md:grid-cols-2 gap-3 mt-3 text-sm">
        <label>What went well (one per line)<Textarea rows={2} value={f.strengths} onChange={(e) => setF({ ...f, strengths: e.target.value })} data-testid="review-strengths-input" /></label>
        <label>What to improve (one per line)<Textarea rows={2} value={f.improvements} onChange={(e) => setF({ ...f, improvements: e.target.value })} data-testid="review-improvements-input" /></label>
        <label>Note to the student<Textarea rows={2} value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} data-testid="review-comment-input" /></label>
        <label>Extra practice to assign (optional)<input value={f.assign_extra} onChange={(e) => setF({ ...f, assign_extra: e.target.value })} className={inp} data-testid="review-extra-input" /></label>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <select value={reviewer} onChange={(e) => setReviewer(e.target.value)} className="border rounded-xl px-3 py-2 text-sm" data-testid="review-reviewer-select">
          {eligible.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>
        <button onClick={() => decide('revise')} disabled={!allScored} className="text-sm font-semibold text-amber-700 disabled:opacity-40" data-testid="review-revise-btn">Request revision</button>
        <PrimaryButton onClick={() => decide('approve')} disabled={!allScored} data-testid="review-approve-btn">Approve</PrimaryButton>
        {!allScored && <span className="text-xs text-slate-500">Score every checklist item to continue</span>}
      </div>
    </Card>
  );
};

export const AdminReviews = ({ catalog }) => {
  const [list, setList] = useState(null);
  const load = useCallback(() => adminApi('/reviews').then(setList).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4 max-w-5xl" data-testid="admin-review-queue">
      <p className="text-sm text-slate-600">Every student submission waits here for a consultant. The student's next task opens only after approval. Projects marked "Specialist review" need a specialist for that role, and each project approval uses one review from the student's program.</p>
      {list && !list.length && <Card className="text-sm text-slate-500" data-testid="review-queue-empty">No submissions waiting for review.</Card>}
      {list?.map((s) => <ReviewCard key={s.id} sub={s} catalog={catalog} onDone={load} />)}
    </div>
  );
};
